// PCs & games (spec/25 §16.1): the venue's boot server, the Windows images its PCs start and the games they see, as three
// pages under one header — Boot server, Windows images and Games. The page is in the address (`?page=images`; the Boot
// server page, the landing, has none), so a link lands on it and the back button undoes a page change.
//
// Boot server: its status in a sentence, its drives and software, how its PCs are doing and its settings. Windows images and
// Games: one card each, with what can be done to it (`ImagesView`, one component for both kinds).
//
// The section used to be "Clavian Boot" with the tabs Servers, Images, Health and Settings (`?tab=`). An old link still
// opens: `?tab=images` becomes `?page=images`, and `?tab=servers`, `health`, `settings` and `pcs` open the Boot server page
// (`bootPageOfLegacyTab`, applied by the route before it loads). The PCs tab was folded into the Floor (2026-10-17).
//
// `boot:view` shows the screen and `boot:manage`, `boot:edit_image` and `boot:wipe` its actions; the server checks each
// again on every request (spec/13 §1). Every boot route needs the organisation's Clavian Boot add-on: without it the api
// answers `402`, and the page says so kindly instead of failing (`NotOnPlan`).

import { useTranslation } from '@clavian/i18n/react';
import { Tabs, type TabItem } from '@clavian/ui-web';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useAccess } from '../api/queries';
import { LoadFailure } from '../shell/LoadFailure';
import { useCurrentVenue } from '../shell/scope';
import { Forbidden, Page } from '../ui';
import { ImagesView, ServerEditRefusals } from './ImagesView';
import { NotOnPlan } from './NotOnPlan';
import { isNotOnPlan, useBootServers } from './queries';
import { useBootLive } from './realtime';
import { ServerPage } from './ServerPage';

import './boot.css';

export const BOOT_PAGES = ['server', 'images', 'games'] as const;
export type BootPageId = (typeof BOOT_PAGES)[number];

export const BOOT_VIEW = 'boot:view';
export const BOOT_MANAGE = 'boot:manage';
export const BOOT_EDIT_IMAGE = 'boot:edit_image';
export const BOOT_WIPE = 'boot:wipe';

/**
 * The page an old `?tab=` link meant: Images is Windows images; Servers, Health, Settings and the removed PCs tab are all on
 * the Boot server page. Undefined for a value that was never a tab.
 */
export function bootPageOfLegacyTab(tab: unknown): BootPageId | undefined {
  if (tab === 'images') return 'images';
  return tab === 'servers' || tab === 'health' || tab === 'settings' || tab === 'pcs' ? 'server' : undefined;
}

/** The address of a page: the landing (Boot server) carries nothing. */
export const bootSearchOf = (page: BootPageId): { page?: BootPageId } => (page === 'server' ? {} : { page });

/**
 * The route's `validateSearch`: a page it knows (the Boot server page is the landing and is not written), or an old `tab`,
 * kept only so the route can send the link to its page.
 */
export function readBootSearch(search: Record<string, unknown>): { page?: BootPageId; tab?: string } {
  const page = search['page'];
  if (typeof page === 'string' && (BOOT_PAGES as readonly string[]).includes(page) && page !== 'server') return { page: page as BootPageId };
  const tab = search['tab'];
  return typeof tab === 'string' && bootPageOfLegacyTab(tab) !== undefined ? { tab } : {};
}

/** What a member may do on these pages. */
export interface BootAccess {
  readonly canManage: boolean;
  readonly canEditImage: boolean;
  readonly canWipe: boolean;
}

export function BootPage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const scope = { organizationId: venue.organizationId, venueId: venue.id };
  const access = useAccess({ venueId: venue.id });
  const ready = access.status === 'ready';
  const canView = ready && access.can(BOOT_VIEW);
  const navigate = useNavigate();
  const search = readBootSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const page: BootPageId = search.page ?? bootPageOfLegacyTab(search.tab) ?? 'server';
  const servers = useBootServers(scope, canView);
  useBootLive(scope, canView && servers.isSuccess);

  const rights: BootAccess = {
    canManage: ready && access.can(BOOT_MANAGE),
    canEditImage: ready && access.can(BOOT_EDIT_IMAGE),
    canWipe: ready && access.can(BOOT_WIPE),
  };

  if (ready && !canView) return <Forbidden message={t('boot.forbidden')} />;
  const header = { title: t('boot.title'), description: t('boot.intro') };
  if (servers.isError) {
    return (
      <Page {...header}>
        {isNotOnPlan(servers.error) ? <NotOnPlan /> : <LoadFailure error={servers.error} message={t('boot.unavailable')} onRetry={() => void servers.refetch()} />}
      </Page>
    );
  }

  const pages: readonly TabItem[] = [
    { value: 'server', label: t('boot.pages.server'), content: <ServerPage venue={venue} scope={scope} servers={servers} access={rights} /> },
    { value: 'images', label: t('boot.pages.images'), content: <ImagesView scope={scope} access={rights} kind="system" /> },
    { value: 'games', label: t('boot.pages.games'), content: <ImagesView scope={scope} access={rights} kind="game" /> },
  ];

  return (
    <Page {...header}>
      {ready && !rights.canManage ? <p className="console-muted">{t('boot.readOnly')}</p> : null}
      <ServerEditRefusals scope={scope} />
      <Tabs
        label={t('boot.pages.label')}
        items={pages}
        value={page}
        onValueChange={(next) => void navigate({ to: '.', search: bootSearchOf(next as BootPageId) } as never)}
        className="console-page-tabs"
      />
    </Page>
  );
}
